import { useMemo, useState } from "react";
import {
  History, Lock, Unlock, GitCompareArrows, Camera, Upload, CalendarClock,
  FileClock, ArrowUpRight, ImageOff, Link2Off, Image as ImageIcon, MapPin,
} from "lucide-react";

const STAGE_LABEL = { move_in: "Move-in", during_tenancy: "During tenancy", move_out: "Move-out" };
const DATE_LABEL = {
  client_captured_at: "Captured by TenantSeal", uploaded_at: "Uploaded record",
  exif_timestamp: "Photo metadata date", user_entered_date: "User-entered date",
  provided_date: "Provided date", record_created_at: "Record created",
};
const DATE_ICON = {
  client_captured_at: Camera, uploaded_at: Upload, exif_timestamp: ImageIcon,
  user_entered_date: CalendarClock, provided_date: CalendarClock, record_created_at: FileClock,
};

function fmtDate(iso) {
  if (!iso) return "Undated";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// Thumbnail (adds a persistent SAMPLE watermark in Demo Mode). Falls back to a clear placeholder.
function Thumb({ url, demo, alt }) {
  const [err, setErr] = useState(false);
  if (!url || err) return (
    <div className="grid place-items-center bg-slate-100 text-[#94A3B8] h-full w-full" data-testid="ch-thumb-error">
      <ImageOff size={16} />
    </div>
  );
  return (
    <div className="relative h-full w-full overflow-hidden">
      <img src={url} alt={alt} onError={() => setErr(true)} className="h-full w-full object-cover" />
      {demo && <span className="absolute bottom-0.5 right-0.5 text-[7px] font-bold tracking-widest text-white/90 bg-black/45 px-1 py-0.5 rounded">SAMPLE</span>}
    </div>
  );
}

function EvidenceCard({ m, demo, onOpen }) {
  const DIcon = DATE_ICON[m.date_source] || FileClock;
  return (
    <div data-testid={`ch-moment-${m.evidence_id}`}
      className="shrink-0 w-[168px] rounded-xl border border-[#E2E8F0] bg-white overflow-hidden">
      <div className="aspect-[4/3] bg-slate-100"><Thumb url={m.thumbnailUrl} demo={demo} alt={m.room_name} /></div>
      <div className="p-2.5 space-y-1.5">
        <span data-testid={`ch-stage-${m.evidence_id}`}
          className="inline-block text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-navy/5 text-navy border border-navy/10">
          {STAGE_LABEL[m.stage] || m.stage}
        </span>
        <p className="text-xs font-semibold text-navy leading-tight">{fmtDate(m.display_date)}</p>
        <p className="flex items-center gap-1 text-[10px] text-[#64748B]" data-testid={`ch-datesrc-${m.evidence_id}`}>
          <DIcon size={11} /> {DATE_LABEL[m.date_source] || "Date"}
        </p>
        <p className={`flex items-center gap-1 text-[10px] font-semibold ${m.sealed ? "text-emerald-700" : "text-amber-700"}`}
          data-testid={`ch-sealed-${m.evidence_id}`}>
          {m.sealed ? <Lock size={11} /> : <Unlock size={11} />} {m.sealed ? "Sealed" : "Not yet sealed"}
        </p>
        {m.note && <p className="text-[11px] text-[#475569] line-clamp-2">{m.note}</p>}
        {(m.photo_category === "monthly_checkup" || m.photo_category === "maintenance") && (
          <p className="flex items-center gap-1 text-[10px] font-semibold text-brand" data-testid={`ch-roomconfirmed-${m.evidence_id}`}>
            <MapPin size={11} /> Room confirmed by you
          </p>
        )}
        <button data-testid={`ch-open-${m.evidence_id}`} onClick={() => onOpen && onOpen(m)}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand hover:underline">
          Open evidence <ArrowUpRight size={12} />
        </button>
      </div>
    </div>
  );
}

// Shared, API-agnostic presentational layer. Reused by the real page and Demo Mode.
// Each moment must already carry `thumbnailUrl`. No grouping/scoring logic lives here.
export function ConditionHistoryView({ address, groups = [], excludedCount = 0, linkedCount = 0, demo = false, onOpenEvidence, onOpenProofReplay }) {
  const [stage, setStage] = useState("all");
  const [groupKey, setGroupKey] = useState("all");

  const stages = ["all", "move_in", "during_tenancy", "move_out"];
  const visibleGroups = useMemo(() => {
    return groups
      .filter((g) => groupKey === "all" || g.group_key === groupKey)
      .map((g) => ({ ...g, moments: g.moments.filter((m) => stage === "all" || m.stage === stage) }))
      .filter((g) => g.moments.length > 0);
  }, [groups, stage, groupKey]);

  return (
    <div data-testid="condition-history-view" className="space-y-4">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-[#94A3B8]">Condition History</p>
        <h1 className="font-heading text-xl font-bold text-navy leading-tight">{address || "Your rental"}</h1>
        <p className="mt-1 text-xs text-[#64748B]">
          Your condition evidence in chronological order across move-in, during tenancy and move-out.
          {linkedCount > 0 && " Combines your linked tenancy-stage records."} This organizes your evidence — it does not judge condition, damage, fault or outcome.
        </p>
      </div>

      {/* Lifecycle filter */}
      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="ch-stage-filter">
        {stages.map((s) => (
          <button key={s} data-testid={`ch-stage-filter-${s}`} onClick={() => setStage(s)}
            className={`whitespace-nowrap text-[11px] font-semibold px-2.5 py-1.5 rounded-full border ${stage === s ? "bg-navy text-white border-navy" : "bg-white text-[#475569] border-[#E2E8F0]"}`}>
            {s === "all" ? "All" : STAGE_LABEL[s]}
          </button>
        ))}
      </div>

      {/* Room / location selector */}
      {groups.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="ch-room-selector">
          <button data-testid="ch-room-all" onClick={() => setGroupKey("all")}
            className={`whitespace-nowrap text-[11px] font-semibold px-2.5 py-1.5 rounded-full border ${groupKey === "all" ? "bg-brand text-white border-brand" : "bg-white text-[#475569] border-[#E2E8F0]"}`}>
            All locations
          </button>
          {groups.map((g) => (
            <button key={g.group_key} data-testid={`ch-room-${g.group_key}`} onClick={() => setGroupKey(g.group_key)}
              className={`whitespace-nowrap text-[11px] font-semibold px-2.5 py-1.5 rounded-full border ${groupKey === g.group_key ? "bg-brand text-white border-brand" : "bg-white text-[#475569] border-[#E2E8F0]"}`}>
              {g.label}
            </button>
          ))}
        </div>
      )}

      {visibleGroups.length === 0 && (
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 text-center text-sm text-[#64748B]" data-testid="ch-view-empty">
          No evidence matches this filter.
        </div>
      )}

      {visibleGroups.map((g) => (
        <div key={g.group_key} data-testid={`ch-group-${g.group_key}`} className="rounded-2xl border border-[#E2E8F0] bg-white p-4">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <h2 className="font-heading text-base font-bold text-navy">{g.label}</h2>
            <span className="text-[10px] text-[#94A3B8]">{g.moments.length} moment{g.moments.length === 1 ? "" : "s"}</span>
            {g.has_proof_replay_pair && (
              <button data-testid={`ch-proofreplay-${g.group_key}`} onClick={() => onOpenProofReplay && onOpenProofReplay(g)}
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-brand bg-brand/10 border border-brand/20 rounded-full px-2 py-0.5 hover:bg-brand/15">
                <GitCompareArrows size={11} /> Proof Replay pair
              </button>
            )}
            {linkedCount > 0 && !g.matched_across_stages && (
              <span data-testid={`ch-unmatched-${g.group_key}`}
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#64748B] bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
                <Link2Off size={11} /> Not matched across tenancy stages
              </span>
            )}
          </div>
          <div className="flex gap-2.5 overflow-x-auto pb-1" data-testid={`ch-strip-${g.group_key}`}>
            {g.moments.map((m) => <EvidenceCard key={m.evidence_id} m={m} demo={demo} onOpen={onOpenEvidence} />)}
          </div>
        </div>
      ))}

      {excludedCount > 0 && (
        <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3 text-[11px] text-[#64748B]" data-testid="ch-excluded-note">
          <span className="inline-flex items-center gap-1 font-semibold text-[#475569]"><History size={12} /> {excludedCount} during-tenancy record{excludedCount === 1 ? "" : "s"} not shown</span>
          {" — "}these lack a confirmed room or location. Add a confirmed location where the option exists to include them.
        </div>
      )}
    </div>
  );
}
